// @flow
import { useCallback, useMemo, useState } from 'react';
import useHouseholdPetsQuery from './useHouseholdPetsQuery';
import { buildApplicationPets } from './useHouseholdPetsQuery.helpers';
import type { ApplicantPetApiRecord, PetViewModel } from '../types';

const usePetsSection = ({
  organizationId,
  propertyId,
  residentId,
  applicants,
  isApplicantSubject,
  canEdit,
}: {
  organizationId: string,
  propertyId: string,
  residentId: ?string,
  applicants: ?Array<ApplicantPetApiRecord>,
  isApplicantSubject: boolean,
  canEdit: boolean,
}) => {
  // Keep the resident query disabled without a resident ID.
  const {
    data: residentPets,
    isLoading: isLoadingResidentPets,
    isError: isResidentPetsError,
  } = useHouseholdPetsQuery({
    organizationId,
    propertyId,
    residentId,
  });

  const applicantPets = useMemo(
    () => (isApplicantSubject ? buildApplicationPets(applicants) : []),
    [isApplicantSubject, applicants],
  );

  const pets = isApplicantSubject ? applicantPets : residentPets;
  const isLoading = isApplicantSubject ? false : isLoadingResidentPets;
  const isError = isApplicantSubject ? false : isResidentPetsError;

  const [selectedPet, setSelectedPet] = useState<?PetViewModel>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAddPet = useCallback(() => {
    setSelectedPet(null);
    setIsModalOpen(true);
  }, []);

  const handleSelectPet = useCallback((pet: PetViewModel) => {
    setSelectedPet(pet);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPet(null);
  }, []);

  /** Allows selection only for editable, non-denied pets. */
  const isPetClickable = useCallback(
    (pet: PetViewModel) => canEdit && pet.kind !== 'denied',
    [canEdit],
  );

  return {
    pets,
    isLoading,
    isError,
    // Report an empty state only after successful reads.
    isEmpty: !isLoading && !isError && pets.length === 0,
    selectedPet,
    isModalOpen,
    handleAddPet,
    handleSelectPet,
    handleCloseModal,
    isPetClickable,
  };
};

export default usePetsSection;
